import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { debounce, localize } from '@saviynt/common';
import {
  Box,
  Checkbox,
  Icon,
  Loader,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import SaviPopover from '../../../components/SaviPopover';
import {
  AdvancedFilterButton,
  AdvancedFilters,
  CertsControlsMore,
  CertsControlTabs,
  CertsSearchBar,
  DynamicSort,
  RowFilterButton,
} from '../../../components/ViewControls';
import ColumnsFilter from '../../../components/ViewControls/ColumnsFilter/ColumnsFilter';
import GroupByButton from '../../../components/ViewControls/GroupBy/GroupByButton';
import {
  getCertDetailsData,
  getTabCounts,
  resetDynamicSortDetails,
  selectAllBulkAction,
  setAlteastOneIsSelected,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setBulkFooterShow,
  setCheckForBulkAction,
  setDynamicSortDetails,
  setIndeterminateForBulkAction,
  setSelectedRowFilters,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  getItemsApiOffset,
  GROUP_BY_APP_VALUE,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_SIZE_TABLET_MEDIUM,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';

import {
  BUTTON_KINDS,
  INITIAL_CONTROL_BUTTON_CONDITIONS,
  OFFSET,
  SEARCHBAR_OFFESET,
} from './constants';
import invertedMessages from './messages';

import './CertsControlsGroup.css';

// TODO: @Vishnu, add prop validation for SaviIntroPopover. Also, move this to a new file (SaviIntroPopover).
function SaviIntroPopover({
  // eslint-disable-next-line react/prop-types
  title,
  // eslint-disable-next-line react/prop-types
  children,
  // eslint-disable-next-line react/prop-types
  showSaviCompanionButtonPopup,
  // eslint-disable-next-line react/prop-types
  setShowSaviCompanionButtonPopup,
  // eslint-disable-next-line react/prop-types
  saviIntoDescription,
}) {
  return showSaviCompanionButtonPopup ? (
    <SaviPopover
      title={title}
      description={saviIntoDescription}
      isOpen={showSaviCompanionButtonPopup}
      setIsOpen={setShowSaviCompanionButtonPopup}>
      {children}
    </SaviPopover>
  ) : (
    children
  );
}

function CertsControlsGroup({
  showFilterBar,
  setShowFilterBar,
  setShowSaviCompanion,
  showSaviCompanionButtonPopup,
  setShowSaviCompanionButtonPopup,
  filterBarRef,
  controlsRef,
  filters,
  setFilters,
  setIsDraggingDndItem,
  setCurrentOffset,
  setMergedLists,
  visibleItemRange,
}) {
  const dispatch = useDispatch();

  // For filter bar
  const [showBadgeOnFilterButton, setShowBadgeOnFilterButton] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  // For group by filter
  const [isBadgeGroupBy, setIsBadgeGroupBy] = useState(false);
  // For row filter
  const [showbadgeOnRowButton, setShowbadgeOnRowButton] = useState(false);
  const [rowFilters, setRowFilters] = useState([]);
  const [appliedRowFilters, setAppliedRowFilters] = useState([]);
  // For column filter
  const [colFilters, setColFilters] = useState([]);
  const [appliedColFilters, setAppliedColFilters] = useState({});
  // For Select All
  const [isSelectAllVisible, setIsSelectAllVisible] = useState(false);
  const [showTotalResults, setShowTotalResults] = useState(false);

  // Search
  const [isSearchBarExpanded, setIsSearchBarExpanded] = useState(false);
  const [isSearchAlert, setIsSearchAlert] = useState(false);
  // More button responsivness
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isButtonSelectLabelVisible, setIsButtonSelectLabelVisible] =
    useState(true);
  const [optionsInMore, setOptionsInMore] = useState([]);
  const [buttonConditions, setButtonConditions] = useState(
    INITIAL_CONTROL_BUTTON_CONDITIONS
  );
  const controlButtonsRef = useRef(null);
  const expandedObserverTargetRef = useRef(null);
  const noSearchObserverTargetRef = useRef(null);
  const iconSizedButtonsObserverTargetRef = useRef(null);
  const conditionalButtonsRef = useRef(null);

  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);
  const l = useInvertedLocalize(invertedMessages);

  const {
    bulkAction: {
      alert: bulkAlert,
      bulkActionProgress,
      isBulkActionEnabled,
      isChecked: isBulkChecked,
      isDisabled: isBulkDisabled,
      isIndeterminate: isBulkIndeterminate,
      isSelecting: isBulkSelecting,
      isSubmittingAction: isBulkSubmittingAction,
    },
    core: {
      columnNames,
      isLoading,
      searchText,
      totalItems,
      selectAllCount,
      campaignType,
    },
    global: { backNavigation, certKey, mediaQueryValue, viewRowsList },
    reviewAndSubmit: { shouldShowReviewAndSubmitButton },
    saviCompanion: { isSaviEnabled },
    sidePanel: { isSidePanelVisible },
    tabs: { tabData },
    viewControls: { groupByOptions },
    lineActions: { reassignConfigMap },
  } = useSelector((state) => state?.certifications?.details);

  const activeTabCount = tabData?.find((tab) => tab?.isActive)?.count;
  const { isSecondaryCertifierLoggedIn } = reassignConfigMap;
  const tabletMediumQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_MEDIUM
  );

  const mobileLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );

  const isMobileXlargeWidth = mobileLargeQuery.value <= mediaQueryValue;
  const isTabletMediumWidth = tabletMediumQuery.value <= mediaQueryValue;

  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;
  const isUsingGroupBy = selectedGroupByValue === GROUP_BY_APP_VALUE;

  const headerClasses = classnames(
    'CertsControlsGroup',
    showFilterBar && 'CertsControlsGroup--isFilterBar',
    tabData?.length && `CertsControlsGroup-tabLength--${tabData?.length}`,
    isSearchBarExpanded && 'CertsControlsGroup--isSearchBarExpanded'
  );
  const selectAllClasses = classnames(
    'CertsControlsGroup-selectAll',
    isSelectAllVisible && 'CertsControlsGroup-selectAll--isSelectAllVisible'
  );
  const selectAllAreaClasses = classnames(
    'CertsControlsGroup-selectAllArea',
    !isSelectAllVisible &&
      !showTotalResults &&
      'CertsControlsGroup-selectAllArea--noContent'
  );
  const controlsGroupClasses = classnames('CertsControlsGroup-tabsAndControls');
  const controlButtonClasses = classnames(
    'CertsControlsGroup-controlButtons',
    isSearchAlert && 'CertsControlsGroup-controlButtons--isSearchAlert'
  );

  // Function to find the button condition by id
  const getVisibleCondition = (id) =>
    buttonConditions.find((condition) => condition.id === id);

  const isSaviVisible = getVisibleCondition(BUTTON_KINDS.SAVI);
  const isFilterVisible = getVisibleCondition(BUTTON_KINDS.FILTER);
  const isGroupByVisible = getVisibleCondition(BUTTON_KINDS.GROUP_BY);
  const isRowsVisible = getVisibleCondition(BUTTON_KINDS.ROWS);
  const isColumnsVisible = getVisibleCondition(BUTTON_KINDS.COLUMNS);
  const isSortVisible = getVisibleCondition(BUTTON_KINDS.SORT);
  const isMoreVisible = getVisibleCondition(BUTTON_KINDS.MORE);

  // Define the conditional classes for each button based on their visibility
  const conditionalButtonClasses = classnames(
    'CertsControlsGroup-conditionalButtonsArray',
    !isSaviVisible?.isVisible && 'CertsControlsGroup--isSaviInMore',
    !isFilterVisible?.isVisible && 'CertsControlsGroup--isFilterInMore',
    !isGroupByVisible?.isVisible && 'CertsControlsGroup--isGroupByInMore',
    !isRowsVisible?.isVisible && 'CertsControlsGroup--isRowsInMore',
    !isColumnsVisible?.isVisible && 'CertsControlsGroup--isColumnsInMore',
    !isSortVisible?.isVisible && 'CertsControlsGroup--isSortInMore',
    !isMoreVisible?.isVisible && 'CertsControlsGroup--isMoreVisible'
  );

  const getRenderCondition = (id) => {
    const button = buttonConditions.find((condition) => condition.id === id);

    return id === BUTTON_KINDS.MORE ? button.isVisible : button.shouldRender;
  };

  const isSaviRendered = getRenderCondition(BUTTON_KINDS.SAVI);
  const isFilterRendered = getRenderCondition(BUTTON_KINDS.FILTER);
  const isGroupByRendered = getRenderCondition(BUTTON_KINDS.GROUP_BY);
  const shouldShowRowFilterBtn = Boolean(
    viewRowsList && viewRowsList?.length > 1
  );
  const isRowsRendered =
    getRenderCondition(BUTTON_KINDS.ROWS) && shouldShowRowFilterBtn;
  const isSortRendered = getRenderCondition(BUTTON_KINDS.SORT);
  const isMoreRendered = getRenderCondition(BUTTON_KINDS.MORE);

  // set applied row filters to redux store
  useEffect(() => {
    dispatch(setSelectedRowFilters(appliedRowFilters));
  }, [appliedRowFilters]);

  // Set the observer widths on mount
  useEffect(() => {
    // Update the render status of control buttons
    if (
      isSaviEnabled &&
      !backNavigation?.consulteeLoggedIn &&
      !isSecondaryCertifierLoggedIn
    )
      setButtonConditions((prev) =>
        prev.map((button) =>
          button.id === BUTTON_KINDS.SAVI
            ? { ...button, shouldRender: true }
            : button
        )
      );
    // Delay the setting of observer widths by 1 second
    const timerId = setTimeout(() => {
      // Set observer widths
      const currentWidth = controlButtonsRef.current.offsetWidth;
      const newWidth = currentWidth - SEARCHBAR_OFFESET;

      if (expandedObserverTargetRef.current)
        expandedObserverTargetRef.current.style.width = `${newWidth}px`;

      if (noSearchObserverTargetRef.current)
        noSearchObserverTargetRef.current.style.width = `${newWidth}px`;
    }, 1000);

    return () => clearTimeout(timerId);
  }, [
    isSaviEnabled,
    backNavigation?.consulteeLoggedIn,
    isSecondaryCertifierLoggedIn,
  ]);

  // Update Tab Counts on Mount
  useEffect(() => {
    if (certKey) dispatch(getTabCounts(certKey, true));
  }, [certKey]);

  useEffect(() => {
    const selectAllFlag = Boolean(
      isBulkActionEnabled &&
        shouldShowReviewAndSubmitButton &&
        activeTabCount > 0
    );

    setIsSelectAllVisible(selectAllFlag);
  }, [
    isBulkActionEnabled,
    totalItems,
    shouldShowReviewAndSubmitButton,
    tabData,
  ]);

  useEffect(() => {
    const controlsContainer = controlsRef.current;
    const noSearchObserverTarget = noSearchObserverTargetRef.current;
    const expandedObserverTarget = expandedObserverTargetRef.current;

    if (
      !controlsContainer ||
      !noSearchObserverTarget ||
      !expandedObserverTarget
    )
      return;

    const simpleObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Handle Label removal
          const isSearchExpanded =
            entry.target === expandedObserverTarget && isSearchBarExpanded;
          const isSearchNotExpanded =
            entry.target === noSearchObserverTarget && !isSearchBarExpanded;

          if (isSearchExpanded) {
            if (entry.intersectionRatio === 1)
              setButtonConditions((prevConditions) =>
                prevConditions.map((button) =>
                  button.id === BUTTON_KINDS.MORE
                    ? { ...button, isVisible: false }
                    : { ...button, isVisible: true }
                )
              );

            if (isMobileXlargeWidth) {
              setIsButtonSelectLabelVisible(false);
            } else {
              setIsButtonSelectLabelVisible(entry.intersectionRatio === 1);
            }
          }

          if (isSearchNotExpanded) {
            if (entry.intersectionRatio === 1)
              setButtonConditions((prevConditions) =>
                prevConditions.map((button) =>
                  button.id === BUTTON_KINDS.MORE
                    ? { ...button, isVisible: false }
                    : { ...button, isVisible: true }
                )
              );

            if (isMobileXlargeWidth) {
              setIsButtonSelectLabelVisible(false);
            } else {
              setIsButtonSelectLabelVisible(entry.intersectionRatio === 1);
            }
          }
        });
      },
      {
        root: controlsContainer,
        threshold: 1,
      }
    );

    simpleObserver.observe(expandedObserverTarget);
    simpleObserver.observe(noSearchObserverTarget);

    // eslint-disable-next-line consistent-return
    return () => simpleObserver.disconnect();
  }, [
    isSidePanelVisible,
    filters,
    isSearchBarExpanded,
    isTabletMediumWidth,
    isButtonSelectLabelVisible,
  ]);

  useEffect(() => {
    const controlsContainer = controlsRef.current;
    const iconSizedButtonsObserverTarget =
      iconSizedButtonsObserverTargetRef.current;

    if (!controlsContainer || !iconSizedButtonsObserverTarget) return;

    const iconSizedButtonsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isMoreObserverAndLabelsHidden =
            entry.target === iconSizedButtonsObserverTarget &&
            !isButtonSelectLabelVisible;

          if (isMoreObserverAndLabelsHidden) {
            const roundedIntersectionRatio =
              Math.round(entry.intersectionRatio * 1000) / 1000;
            const isVisible = roundedIntersectionRatio > 0.5;
            const isFullyVisible = roundedIntersectionRatio === 1;
            const isAllRenderableButtonsInMore = buttonConditions
              .filter((button) => button.id !== 'More' && button.shouldRender)
              .every((button) => !button.isVisible);
            const isAllRenderableButtonsVisible = buttonConditions
              .filter((button) => button.id !== 'More' && button.shouldRender)
              .every((button) => button.isVisible);

            if (!isVisible && !isAllRenderableButtonsInMore)
              setButtonConditions((prevConditions) => {
                const newConditions = [...prevConditions];

                // Hide the last visible button that isn't "More"
                for (let i = newConditions.length - 1; i >= 0; i -= 1) {
                  if (
                    newConditions[i].isVisible &&
                    newConditions[i].id !== BUTTON_KINDS.MORE
                  ) {
                    newConditions[i].isVisible = false;
                    break; // Stop once the last visible button is hidden
                  }
                }

                return newConditions;
              });

            if (isFullyVisible && !isAllRenderableButtonsVisible)
              setButtonConditions((prevConditions) => {
                const newConditions = [...prevConditions];

                if (isFullyVisible && !isAllRenderableButtonsVisible) {
                  const firstHiddenIndex = newConditions.findIndex(
                    (c) => !c.isVisible && c.id !== BUTTON_KINDS.MORE
                  );

                  if (
                    firstHiddenIndex !== -1 &&
                    !newConditions[firstHiddenIndex].isVisible
                  ) {
                    newConditions[firstHiddenIndex].isVisible = true;
                    setButtonConditions(newConditions);
                  }
                }

                return newConditions;
              });
          }
        });
      },
      {
        root: controlsContainer,
        threshold: Array.from({ length: 1001 }, (_, i) => i * 0.001),
      }
    );

    iconSizedButtonsObserver.observe(iconSizedButtonsObserverTarget);

    // eslint-disable-next-line consistent-return
    return () => iconSizedButtonsObserver.disconnect();
  }, [
    buttonConditions,
    isSidePanelVisible,
    filters,
    isSearchBarExpanded,
    isTabletMediumWidth,
    isButtonSelectLabelVisible,
  ]);

  useEffect(() => {
    if (isButtonSelectLabelVisible)
      setButtonConditions((prevConditions) =>
        prevConditions.map((button) =>
          button.id === BUTTON_KINDS.MORE
            ? { ...button, isVisible: false }
            : { ...button, isVisible: true }
        )
      );
  }, [isButtonSelectLabelVisible]);

  useEffect(() => {
    const isAnyButtonInMore = buttonConditions
      .filter(
        (button) => button.id !== BUTTON_KINDS.MORE && button.shouldRender
      )
      .some((button) => !button.isVisible);
    const moreButton = buttonConditions.find(
      (button) => button.id === BUTTON_KINDS.MORE
    );

    if (isAnyButtonInMore && moreButton.isVisible) return;

    if (!isAnyButtonInMore && moreButton.isVisible)
      setButtonConditions((prevConditions) =>
        prevConditions.map((button) =>
          button.id === BUTTON_KINDS.MORE
            ? { ...button, isVisible: false }
            : button
        )
      );

    if (isAnyButtonInMore && !moreButton.isVisible)
      setButtonConditions((prevConditions) =>
        prevConditions.map((button) =>
          button.id === BUTTON_KINDS.MORE
            ? { ...button, isVisible: true }
            : button
        )
      );
  }, [
    buttonConditions,
    isSidePanelVisible,
    filters,
    isSearchBarExpanded,
    isTabletMediumWidth,
    isButtonSelectLabelVisible,
  ]);

  useEffect(() => {
    if (
      searchText?.length > 0 ||
      (showBadgeOnFilterButton && !isLoading) ||
      (showbadgeOnRowButton && !isLoading) ||
      (isBadgeGroupBy && !isLoading)
    ) {
      setShowTotalResults(true);
    } else {
      setShowTotalResults(false);
    }
  }, [searchText, isLoading, showBadgeOnFilterButton]);

  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      setOptionsInMore(
        buttonConditions
          .filter((button) => button.shouldRender && !button.isVisible)
          .map((button) => button.id)
      );
    }, 50);

    debouncedUpdate();

    return () => debouncedUpdate.cancel();
  }, [buttonConditions]);

  // Sync local Group By and redux arrays
  useEffect(() => {
    setIsBadgeGroupBy(isUsingGroupBy);
  }, [isUsingGroupBy]);

  const handleBulkChange = () => {
    dispatch(setBulkActionBySelectAll(true));

    if (isBulkIndeterminate) {
      const prevBulkCheckStatus = isBulkChecked;
      const prevBulkIndeterminateStatus = isBulkIndeterminate;

      dispatch(setIndeterminateForBulkAction(false));
      dispatch(setCheckForBulkAction(true));

      dispatch(selectAllBulkAction(certKey))
        .then(() => {
          dispatch(setBulkFooterShow(true));
        })
        .catch((error) => {
          dispatch(setBulkActionBySelectAll(true));
          dispatch(setBulkActionAlert(true, error.message));
          dispatch(setIndeterminateForBulkAction(prevBulkIndeterminateStatus));
          dispatch(setCheckForBulkAction(prevBulkCheckStatus));
        });

      return;
    }

    const prevBulkCheckStatus = isBulkChecked;

    if (prevBulkCheckStatus) {
      dispatch(selectAllBulkAction(certKey, true))
        .then(() => {
          dispatch(setCheckForBulkAction(false));
          dispatch(setBulkFooterShow(false));
          dispatch(setAlteastOneIsSelected(false));
        })
        .catch((error) => {
          dispatch(setBulkActionAlert(true, error.message));
        });

      return;
    }

    dispatch(setCheckForBulkAction(true));
    dispatch(selectAllBulkAction(certKey))
      .then(() => {
        dispatch(setBulkFooterShow(true));
      })
      .catch((error) => {
        dispatch(setBulkActionBySelectAll(true));
        dispatch(setBulkActionAlert(true, error.message));
        dispatch(setCheckForBulkAction(prevBulkCheckStatus));
      });
  };

  const resetMergeList = () => {
    setCurrentOffset(0);
    setMergedLists([]);
    dispatch(
      getCertDetailsData(
        certKey,
        columnNames,
        getItemsApiOffset(activeTabCount),
        OFFSET,
        searchText
      )
    );
  };

  const resetSort = () => {
    dispatch(resetDynamicSortDetails());
    resetMergeList();
  };

  const handleDynamicSort = (selectedOption, sortDirection) => {
    if (selectedOption)
      dispatch(setDynamicSortDetails(selectedOption, sortDirection || 'asc'));
  };

  return (
    <Box
      tag='section'
      className={headerClasses}
      dataTestId='details-controls-group'>
      <StackingAlertBanner
        colorTheme='Critical'
        title={
          messages.CERTS_ACTION_FAILED ||
          localize(intl, getMessageKey('CERTS_ACTION_FAILED'))
        }
        description={bulkAlert?.alertText || ''}
        isVisible={bulkAlert?.isAlert}
        onCancel={() => dispatch(setBulkActionAlert(false, ''))}
      />
      <Box tag='div' className={controlsGroupClasses}>
        <CertsControlTabs />
        <div className='CertsControlsGroup-controls' ref={controlsRef}>
          <div className='CertsControlsGroup-spacingObservers'>
            <div
              ref={expandedObserverTargetRef}
              className='CertsControlsGroup-expandedObserverTarget'
            />
            <div
              ref={noSearchObserverTargetRef}
              className='CertsControlsGroup-noSearchObserverTarget'
            />
          </div>
          <div className={controlButtonClasses} ref={controlButtonsRef}>
            <div
              className={conditionalButtonClasses}
              ref={conditionalButtonsRef}>
              <div
                ref={iconSizedButtonsObserverTargetRef}
                className='CertsControlsGroup-iconSizedButtonsObserverTarget'
              />
              {/* Not showing savi for SA campaign */}
              {isSaviRendered &&
                !isSecondaryCertifierLoggedIn &&
                !backNavigation.consulteeLoggedIn &&
                !isSACampaign && (
                  <SaviIntroPopover
                    title={
                      messages.CERTS_SAVI_RECOMMENDATIONS ||
                      localize(
                        intl,
                        getMessageKey('CERTS_SAVI_RECOMMENDATIONS')
                      )
                    }
                    showSaviCompanionButtonPopup={showSaviCompanionButtonPopup}
                    setShowSaviCompanionButtonPopup={
                      setShowSaviCompanionButtonPopup
                    }
                    saviIntoDescription={
                      messages.CERTS_SAVI_INTRO_DESCRIPTION ||
                      localize(
                        intl,
                        getMessageKey('CERTS_SAVI_INTRO_DESCRIPTION')
                      )
                    }>
                    <div
                      role='button'
                      aria-label='SaviCompanion'
                      className='Savi-Companion-Button'
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setShowSaviCompanion(true);
                        }
                      }}
                      onClick={() => setShowSaviCompanion(true)}>
                      <Icon kind='SaviCompanionGradient' size='smallMedium' />
                    </div>
                  </SaviIntroPopover>
                )}
              {isFilterRendered && (
                <AdvancedFilterButton
                  filters={filters}
                  showFilterBar={showFilterBar}
                  setShowFilterBar={setShowFilterBar}
                  showBadgeOnFilterButton={showBadgeOnFilterButton}
                  isButtonKindIcon={isButtonSelectLabelVisible}
                />
              )}
              {isGroupByRendered && (
                <GroupByButton
                  isBadgeGroupBy={isBadgeGroupBy}
                  groupByOptions={groupByOptions}
                  isButtonKindIcon={isButtonSelectLabelVisible}
                  label={
                    messages.CERTS_GROUP_BY ||
                    localize(intl, getMessageKey('CERTS_GROUP_BY'))
                  }
                  resetMergeList={resetMergeList}
                />
              )}
              {isRowsRendered && (
                <RowFilterButton
                  showbadgeOnRowButton={showbadgeOnRowButton}
                  setShowbadgeOnRowButton={setShowbadgeOnRowButton}
                  rowFilters={rowFilters}
                  setRowFilters={setRowFilters}
                  appliedRowFilters={appliedRowFilters}
                  setAppliedRowFilters={setAppliedRowFilters}
                  isButtonKindIcon={isButtonSelectLabelVisible}
                  label={
                    messages.CERTS_ROWS ||
                    localize(intl, getMessageKey('CERTS_ROWS'))
                  }
                />
              )}
              {/* ColumnsFilter render conditions are not simply based on should render. As the setAppliedColFilters logic inside need to be accessed on mount for in more and on Mobile width below 480px */}
              <ColumnsFilter
                colFilters={colFilters}
                setColFilters={setColFilters}
                appliedColFilters={appliedColFilters}
                setAppliedColFilters={setAppliedColFilters}
                isButtonKindIcon={isButtonSelectLabelVisible}
                setIsDraggingDndItem={setIsDraggingDndItem}
              />
              {isSortRendered && !isMoreRendered && (
                <DynamicSort
                  rowFilters={rowFilters}
                  colFilters={colFilters}
                  appliedRowFilters={appliedRowFilters}
                  handleSort={handleDynamicSort}
                  resetDynamicSort={resetSort}
                  isButtonKindIcon={isButtonSelectLabelVisible}
                />
              )}
              {isMoreRendered && (
                <CertsControlsMore
                  options={optionsInMore}
                  setIsMoreOpen={setIsMoreOpen}
                  // Sort
                  handleDynamicSort={handleDynamicSort}
                  resetDynamicSort={resetSort}
                  // Group By
                  groupByOptions={groupByOptions}
                  // Filter
                  showFilterBar={showFilterBar}
                  setShowFilterBar={setShowFilterBar}
                  // Rows
                  rowFilters={rowFilters}
                  appliedRowFilters={appliedRowFilters}
                  setAppliedRowFilters={setAppliedRowFilters}
                  // Columns
                  colFilters={colFilters}
                  setColFilters={setColFilters}
                  appliedColFilters={appliedColFilters}
                  setAppliedColFilters={setAppliedColFilters}
                  setIsDraggingDndItem={setIsDraggingDndItem}
                />
              )}
            </div>
            <CertsSearchBar
              isMoreOpen={isMoreOpen}
              isSearchBarExpanded={isSearchBarExpanded}
              setIsSearchBarExpanded={setIsSearchBarExpanded}
              isSearchAlert={isSearchAlert}
              setIsSearchAlert={setIsSearchAlert}
            />
          </div>
        </div>
      </Box>
      <Box tag='div' className='CertsControlsGroup-advancedFilters'>
        <AdvancedFilters
          setShowBadgeOnFilterButton={setShowBadgeOnFilterButton}
          showFilterBar={showFilterBar}
          setShowFilterBar={setShowFilterBar}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          filterBarRef={filterBarRef}
          appliedRowFilters={appliedRowFilters}
          rowFilters={rowFilters}
        />
      </Box>
      <div className={selectAllAreaClasses}>
        {isSelectAllVisible && (
          <Box tag='div' className={selectAllClasses}>
            <Checkbox
              id='simple-text-certs-controls-group-select-all'
              name='simpleText'
              isChecked={isBulkChecked}
              isDisabled={
                selectAllCount === 0 ||
                isBulkDisabled ||
                isBulkSubmittingAction ||
                isLoading ||
                bulkActionProgress?.inProgress
              }
              isIndeterminate={isBulkIndeterminate}
              onChange={handleBulkChange}
              label={`${
                messages['SELECTALL.ATTESTATIONS'] ||
                localize(intl, getMessageKey('SELECTALL.ATTESTATIONS'))
              } (${selectAllCount || 0})`}
              textPosition='after'
              showHoverEffect
              dataTestId='simpleText'
            />
            {isBulkSelecting && (
              <Loader kind='loop' format='inline' color='brand' size='small' />
            )}
          </Box>
        )}

        {/*
          CR-21415: Displays "Showing Records: X of Y" where X is the 1-based index
          of the last fully visible tile in the viewport, and Y is the total record count.
          Updates dynamically as the user scrolls.
        */}
        <Box tag='div' className='CertsControlsGroup-resultsInfo'>
          <div className='CertsControlsGroup-loaded--info'>
            <Typography kind='body2'>
              {l('Showing Records: {lastVisible} of {totalItems}', {
                lastVisible: visibleItemRange.end,
                totalItems,
              })}
            </Typography>
          </div>
        </Box>
      </div>
    </Box>
  );
}

CertsControlsGroup.propTypes = {
  showFilterBar: PropTypes.bool.isRequired,
  setShowFilterBar: PropTypes.func.isRequired,
  setShowSaviCompanion: PropTypes.func.isRequired,
  showSaviCompanionButtonPopup: PropTypes.bool,
  setShowSaviCompanionButtonPopup: PropTypes.func,
  controlsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  filterBarRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
  setIsDraggingDndItem: PropTypes.func.isRequired,
  setCurrentOffset: PropTypes.func.isRequired,
  setMergedLists: PropTypes.func.isRequired,
  visibleItemRange: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }).isRequired,
};

CertsControlsGroup.defaultProps = {
  showSaviCompanionButtonPopup: false,
  setShowSaviCompanionButtonPopup: () => {},
  controlsRef: null,
  filterBarRef: null,
};

export default CertsControlsGroup;
